/**
 * Low-precision astronomy for the top sky: where the sun and moon are above
 * a given place, right now, and whether the site should be light or dark.
 * Accurate to about a degree, which is far finer than one dot on screen.
 */

const RAD = Math.PI / 180
const DEG = 180 / Math.PI

/** Position in the local sky, in degrees. Azimuth runs clockwise from north. */
export interface SkyPosition {
  readonly elevation: number
  readonly azimuth: number
}

export interface MoonState extends SkyPosition {
  /** Lit fraction of the disc, 0 (new) to 1 (full). */
  readonly illumination: number
  /** True while the moon is growing toward full. */
  readonly waxing: boolean
}

const wrap360 = (deg: number) => ((deg % 360) + 360) % 360

/** Days since the J2000.0 epoch (2000-01-01 12:00 UTC). */
function daysSinceJ2000(date: Date): number {
  return date.getTime() / 86_400_000 - 10_957.5
}

/** Obliquity of the ecliptic (deg) for a J2000 day number. */
const obliquity = (d: number) => 23.439 - 0.000_000_4 * d

/** Ecliptic longitude of the sun (deg). */
function sunLongitude(d: number): number {
  const meanLongitude = wrap360(280.46 + 0.985_647_4 * d)
  const anomaly = wrap360(357.528 + 0.985_600_3 * d) * RAD
  return wrap360(meanLongitude + 1.915 * Math.sin(anomaly) + 0.02 * Math.sin(2 * anomaly))
}

/** Convert ecliptic coordinates to elevation/azimuth for an observer. */
function toLocalSky(
  d: number,
  eclipticLon: number,
  eclipticLat: number,
  lat: number,
  lon: number
): SkyPosition {
  const eps = obliquity(d) * RAD
  const l = eclipticLon * RAD
  const b = eclipticLat * RAD

  const ra = Math.atan2(
    Math.sin(l) * Math.cos(eps) - Math.tan(b) * Math.sin(eps),
    Math.cos(l)
  )
  const dec = Math.asin(
    Math.sin(b) * Math.cos(eps) + Math.cos(b) * Math.sin(eps) * Math.sin(l)
  )

  const siderealTime = wrap360(280.460_618_37 + 360.985_647_366_29 * d + lon)
  const hourAngle = (siderealTime - ra * DEG) * RAD
  const phi = lat * RAD

  const elevation = Math.asin(
    Math.sin(phi) * Math.sin(dec) + Math.cos(phi) * Math.cos(dec) * Math.cos(hourAngle)
  )
  const azimuth = Math.atan2(
    -Math.sin(hourAngle),
    Math.tan(dec) * Math.cos(phi) - Math.sin(phi) * Math.cos(hourAngle)
  )

  return { elevation: elevation * DEG, azimuth: wrap360(azimuth * DEG) }
}

/** The sun's position for an observer at `lat`/`lon` at `date`. */
export function sunPosition(date: Date, lat: number, lon: number): SkyPosition {
  const d = daysSinceJ2000(date)
  return toLocalSky(d, sunLongitude(d), 0, lat, lon)
}

/** The moon's position and phase for an observer at `lat`/`lon` at `date`. */
export function moonState(date: Date, lat: number, lon: number): MoonState {
  const d = daysSinceJ2000(date)
  const meanLongitude = 218.316 + 13.176_396 * d
  const anomaly = (134.963 + 13.064_993 * d) * RAD
  const latitudeArg = (93.272 + 13.22935 * d) * RAD

  const eclipticLon = wrap360(meanLongitude + 6.289 * Math.sin(anomaly))
  const eclipticLat = 5.128 * Math.sin(latitudeArg)

  const elongation = wrap360(eclipticLon - sunLongitude(d))
  return {
    ...toLocalSky(d, eclipticLon, eclipticLat, lat, lon),
    illumination: (1 - Math.cos(elongation * RAD)) / 2,
    waxing: elongation < 180,
  }
}

// ============================================================================
// Colors
// ============================================================================

export type Rgb = readonly [number, number, number]

/** Parse a `#rrggbb` color. */
export const hex = (value: string): Rgb => [
  parseInt(value.slice(1, 3), 16),
  parseInt(value.slice(3, 5), 16),
  parseInt(value.slice(5, 7), 16),
]

export const mixRgb = (a: Rgb, b: Rgb, t: number): Rgb => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
]

/** How dark the sky is: 0 in daylight, 1 once the sun is 14° below the horizon. */
export const nightness = (sunElevation: number) =>
  Math.max(0, Math.min(1, (-sunElevation - 4) / 10))

// ============================================================================
// Site theme
// ============================================================================

export type Theme = "light" | "dark"

/**
 * Sun elevation (deg) below which the site turns dark: the end of civil
 * twilight, when the sky has lost its color and city lights take over.
 */
export const DARK_BELOW = -6

/** The site theme for a sun elevation. */
export const themeForSun = (sunElevation: number): Theme =>
  sunElevation < DARK_BELOW ? "dark" : "light"

/**
 * Page background per theme; clouds and the moon are filled with it. Mirrors `--color-bg`
 * and `--night-bg` in index.css.
 */
export const PAPER: Readonly<Record<Theme, Rgb>> = {
  light: hex("#ffffff"),
  dark: hex("#0e1022"),
}
